import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { useAuthStore } from "@/lib/auth";
import { useQueueStore } from "@/lib/store";
import { DoctorDashboard as DoctorDashboardComponent } from "@/components/dashboard/DoctorDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const DoctorDashboard = () => {
  const { user } = useAuthStore();
  const { patients, fetchPatients, isLoading, error } = useQueueStore();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    if (user?.role === 'doctor') {
      const loadData = async () => {
        try {
          await fetchPatients();
        } catch (error) {
          console.error('Error loading patients:', error);
        } finally {
          setIsInitialLoad(false);
        }
      };
      loadData();
    }
  }, [user?.role, fetchPatients]);

  if (!user || user.role !== 'doctor') {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-gray-500">This page is only accessible to doctors.</p>
        </div>
      </MainLayout>
    );
  }

  // Show loading state
  if (isInitialLoad || isLoading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Doctor Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back, Dr. {user.name}
            </p>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Your Patients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading patients...</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Doctor Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back, Dr. {user.name}
            </p>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Your Patients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <div className="text-red-600 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Patients</h3>
                <p className="text-gray-500 mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Doctor Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, Dr. {user.name}
          </p>
        </div>

        <DoctorDashboardComponent />
      </div>
    </MainLayout>
  );
};

export default DoctorDashboard;
